import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { format, getDay } from 'date-fns';
import { es } from 'date-fns/locale';
import { useMediaQuery } from 'react-responsive';
import { apps } from '~/utils/mocks/apps';
import { Icon, Link, Spinner } from '~/components';
import { getMatchesRequest } from '~/store/modules/matches/actions';
import history from '~/config/history';
import { theme } from '~/styles/theme';
import * as S from './styles';

export default function DashboardContent() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const {
    matches: { data: dates, loading },
  } = useSelector(state => state.matches);
  const { user } = useSelector(state => state.user);
  const { search } = useLocation();

  const isTablet = useMediaQuery({
    query: `(max-width: ${theme.breakpoints.lg}px)`,
  });

  useEffect(() => {
    dispatch(getMatchesRequest(search));
  }, [dispatch, search]);

  const showProcessedDate = date => {
    const newDate = new Date(date);
    const weekday = t(`weekday${getDay(newDate)}`);
    const processedDate = format(
      newDate,
      'dd MMMM yyyy',
      t('languageId') === 'ES'
        ? {
            locale: es,
          }
        : null
    );
    return `${weekday}, ${processedDate}`;
  };

  const datesArr = dates && Array.isArray(dates) ? dates : [];

  return (
    <S.ContentWrapper>
      <Spinner isRelative isOpen={loading} />
      {!loading && datesArr.length <= 0 && <S.EmptyPageDashboard />}
      {!loading && datesArr.length > 0 && (
        <>
          <S.Background />
          {datesArr.map(date => {
            return (
              <S.DayMatches key={`match-${date['@id']}`}>
                <S.DayMatchesHeader>
                  {showProcessedDate(date.date)}
                </S.DayMatchesHeader>
                <S.DayMatchesContent>
                  {date.matches.map(match => {
                    const {
                      homeTeam,
                      awayTeam,
                      id,
                      peninsulaTime,
                      homeScore,
                      awayScore,
                      competitionLogo,
                    } = match;
                    return (
                      <S.Match key={`match-${id}`}>
                        <S.MatchLeft
                          onClick={() =>
                            !loading &&
                            history.push(`/match/${id}/global-statistics`)
                          }
                        >
                          <S.MatchTime>
                            {peninsulaTime ? peninsulaTime.substr(0, 5) : '-'}
                          </S.MatchTime>
                          <S.Teams>
                            <S.LocalTeam>
                              <div>
                                {isTablet
                                  ? homeTeam.shortname
                                  : homeTeam.nickname}
                              </div>
                              <img src={homeTeam.shieldImageUrl} alt="" />
                              <div>
                                {typeof homeScore === 'number' ||
                                typeof homeScore === 'string'
                                  ? homeScore
                                  : '-'}
                              </div>
                            </S.LocalTeam>
                            <S.Separator />
                            <S.VisitorTeam>
                              <div>
                                {typeof awayScore === 'number' ||
                                typeof awayScore === 'string'
                                  ? awayScore
                                  : '-'}
                              </div>
                              <img src={awayTeam.shieldImageUrl} alt="" />
                              <div>
                                {isTablet
                                  ? awayTeam.shortname
                                  : awayTeam.nickname}
                              </div>
                            </S.VisitorTeam>
                          </S.Teams>
                          <S.CompetitionLogo>
                            <img src={competitionLogo} alt="" />
                          </S.CompetitionLogo>
                          <S.ArrowIcon>
                            <Link
                              to={
                                !loading
                                  ? `/match/${id}/global-statistics`
                                  : '/dashboard'
                              }
                            >
                              <Icon
                                name="chevronRight"
                                size={12}
                                color="coral"
                              />
                            </Link>
                          </S.ArrowIcon>
                        </S.MatchLeft>
                        <S.MatchRight>
                          <S.MatchLinks>
                            {apps.map(app => {
                              return (
                                <Link
                                  to={
                                    !loading
                                      ? `/match/${id}/previous/${app.route}`
                                      : '/dashboard'
                                  }
                                  title={t(app.name)}
                                  key={`app-${app.id}`}
                                >
                                  <div>
                                    <Icon
                                      name={app.icon}
                                      size={24}
                                      color="dark"
                                    />
                                  </div>
                                </Link>
                              );
                            })}
                          </S.MatchLinks>
                          {user.laliga && (
                            <S.MatchRedirect color="gray5">
                              <Link
                                to={
                                  !loading
                                    ? `/private-documents/${id}`
                                    : '/dashboard'
                                }
                              >
                                <Icon name="document" size={22} color="white" />
                              </Link>
                            </S.MatchRedirect>
                          )}
                          <S.MatchRedirect color="dark" rotated>
                            <Link
                              to={
                                !loading
                                  ? `/match/${id}/global-statistics`
                                  : '/dashboard'
                              }
                            >
                              <Icon name="chevronUp" size={15} color="white" />
                            </Link>
                          </S.MatchRedirect>
                        </S.MatchRight>
                      </S.Match>
                    );
                  })}
                </S.DayMatchesContent>
              </S.DayMatches>
            );
          })}
        </>
      )}
    </S.ContentWrapper>
  );
}
